<template>
  <div class="area-chart">
    <div class="area-chart__data">
      <apexchart
        ref="stackedChart"
        class="stacked-chart"
        :height="height"
        :options="chartOptions"
        :series="series.series"
        type="bar"
        width="100%"
      />
    </div>

    <!-- Legend -->
    <div class="area-chart-legend-scroll">
      <div class="area-chart__legend">
        <div
          v-for="(item, index) in series.series"
          :key="index"
          :class="areaClass(item)"
        >
          <input
            :id="`history_${item.name}`"
            v-model="currentSelections"
            :disabled="item.data.length <= 0"
            type="checkbox"
            :value="item.name"
            @click="toggleSeries(item.name)"
          >
          <label :for="`history_${item.name}`">
            <span class="area-chart__legend-item-swap">
              <span class="area-chart__legend-item-label">
                {{ legendLabel(item.name) }}
              </span>
              <span class="typo-small-title area-chart__legend-item-count">
                {{ item.count || 0 }}
              </span>
            </span>
            <span class="area-chart__legend-item-percent">
              {{ generatePercentage(item.count) }}
            </span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import VueApexCharts from 'vue3-apexcharts';

import { MONTHS } from '@/functions/date_format_helpers';
import I18n from 'i18n-js';

import {
  getLocales,
  gridOptions,
  historySeriesColours,
  toolBarOptions,
} from '@/functions/chart_config';

const apexchart = VueApexCharts;
const currentSelections = ref([ 'approved', 'provisional' ]);
const stackedChart = ref('stackedChart');

const props = defineProps({
  /** Sets the height of the chart */
  height: { type: Number, required: false, default: 100 },
  /** Unique id for the chart. To ensure there is no clashing with multiple charts */
  id: { type: String, required: true, default: '' },
  /** Series & catagories data */
  series: { type: Object, required: true, default: null },
});

const approvedCount = computed(() => {
  const approvedData = findType('approved');
  if (!approvedData) return;
  const total = approvedData?.data ? countSeries(approvedData?.data) : 0;
  approvedData['count'] = total;
  return total;
});

const provisionalCount = computed(() => {
  const provisionalData = findType('provisional');
  if (!provisionalData) return;
  const total = provisionalData?.data ? countSeries(provisionalData?.data) : 0;
  provisionalData['count'] = total;
  return total;
});

onBeforeMount(() => {
  if (findType('all')) return;
  props.series?.series.push({
    name: 'all',
    count: (approvedCount.value + provisionalCount.value) || 0,
    data: [],
  });
});

function findType(type) {
  return props.series?.series?.find(item => item.name === type);
}

function countSeries(data) {
  return data.reduce((a, b) => a + b, 0);
}

function areaClass(item) {
  const global = `area-chart__legend-item area-chart__legend-item--${item.name} typo-tiny`;
  return `${global} ${item.data.length <= 0 ? 'area-chart__legend-item--disabled' : ''}`;
}

function generatePercentage(count) {
  const total = approvedCount.value + provisionalCount.value;
  if (!count || !total) return '0%';
  return Math.round((count / total) * 100) + '%';
}

/**
 * Toggles graph series for the graph and legend
 */
function toggleSeries(checkbox) {
  stackedChart.value.toggleSeries(checkbox);
}

/**
 * Returns the correct label text for the legend
 */
function legendLabel(item) {
  let chartType = I18n.t(`inspections.${item}`);

  return item === 'all' ? I18n.t('general.total') : chartType;
}

function formatDate(value) {
  let date = new Date(value);
  return `${I18n.t(`months.shorthand.${MONTHS[date.getMonth()]}`)} ${date.getFullYear()}`;
}

/**
 * Colours for chart options
 */
const colourConfig = {
  barColor: '#29A7F4',
  dataLabels: '#D5D4D6',
  foreColor: '#989799',
};

/**
 * Font sizes for chart options
 */
const fontSizeConfig = {
  dataLabels: '12px',
  tooltipLabels: '13px',
};

/**
 * Chart options
 * (defines what the chart can do and what it looks like)
 */
const chartOptions = {
  chart: {
    defaultLocale: I18n.locale,
    foreColor: colourConfig.foreColor,
    id: props.id,
    locales: getLocales(I18n),
    stacked: true,
    toolbar: toolBarOptions,
    type: 'bar',
  },
  yaxis: {
    min: 0.01,
    forceNiceScale: false,
    max: (max) => max,
    labels: {
      formatter: (value) => (value ? value.toFixed(0) : value),
    },
  },
  xaxis: {
    type: 'datetime',
    categories: props.series.categories,
    axisBorder: { show: true },
    position: 'bottom',
    labels: {
      style: {
        fontSize: fontSizeConfig.dataLabels,
        fontWeight: 300,
      },
      datetimeFormatter: {
        formatter: (value) => formatDate(value),
      },
    },
  },
  plotOptions: {
    bar: { horizontal: false },
  },
  legend: { show: false },
  dataLabels: { enabled: false },
  grid: gridOptions,
  colors: [colourConfig.barColor],
  tooltip: {
    shared: false,
    enabled: true,
    followCursor: true,
    fillSeriesColor: false,
    theme: false,
    style: { fontSize: fontSizeConfig.tooltipLabels },
    x: {
      show: true,
      formatter: (value) => formatDate(value),
    },
    y: {
      title: {
        formatter: (seriesName) => `${I18n.t(`inspections.${seriesName}`)}`,
      },
    },
  },
};

chartOptions.colors = historySeriesColours;

const approvedColor = chartOptions.colors[0];
const provisionalColor = chartOptions.colors[1];
</script>

<style lang="scss" scoped>
@import '@/stylesheets/global';
@import '@/stylesheets/components/apexchart_override.scss';

.area-chart {
  &__legend {
    @include breakpoint(large) {
      justify-content: start;
    }
  }

  &__legend-item {
    &--approved {
      label::before {
        background-color: v-bind(approvedColor);
      }
    }

    &--provisional {
      label::before {
        background-color: v-bind(provisionalColor);
      }
    }
  }
}
</style>
